import React, { useState, useEffect } from "react";
import { Avatar, Upload, message, Spin } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useAuth } from "../../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { useUpdateUser } from "../../hooks/useUsers";
import apiClient from "../../api/apiClient";
import { uploadImageToCloudinary } from "../../helpers/cloudinary";

const ProfileDetails: React.FC = () => {
  const { data: authUser } = useAuth();
  const updateUserMutation = useUpdateUser();

  // Fetch detailed user data
  const {
    data: userData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["user", authUser?.id],
    queryFn: async () => {
      if (!authUser?.id) return null;
      const response = await apiClient.get(`/user/${authUser.id}`);
      return response.data.data;
    },
    enabled: !!authUser?.id,
  });

  // State for avatar display
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Update avatarUrl when userData changes
  useEffect(() => {
    if (userData?.avatar) {
      setAvatarUrl(userData.avatar);
    }
  }, [userData]);

  // Function to generate user initials for avatar fallback
  const getInitials = (name: string) => {
    if (!name) return "U";
    const nameParts = name.split(" ");
    if (nameParts.length > 1) {
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Handle avatar upload using Cloudinary
  const handleAvatarUpload = async (file: File) => {
    if (!authUser?.id || !userData) {
      message.error("User data not available");
      return false;
    }

    try {
      // Upload to Cloudinary
      const uploadedUrl = await uploadImageToCloudinary(file);
      setAvatarUrl(uploadedUrl);

      // Update user with new avatar URL using the existing hook
      await updateUserMutation.mutateAsync({
        userId: authUser.id,
        userData: {
          // Include existing user data
          name: userData.name,
          email: userData.email,
          phone: userData.phone || "",
          user_type_id: userData.user_type_id || 0,
          role_id: userData.roles?.id,
          location_id: userData.location_id || 0,
          gender: userData.gender || "male",
          country: userData.country || "",
          state: userData.state || "",
          lga: userData.lga || "",
          status: true,
          // Add the avatar URL
          avatar: uploadedUrl,
        },
      });

      message.success("Avatar updated successfully!");
      refetch(); // Refetch user data

      return false; // Prevent default upload behavior
    } catch (error) {
      console.error("Avatar upload error:", error);
      message.error("Failed to upload avatar. Please try again.");

      return false;
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 bg-white rounded-lg border border-gray-200 mb-6 flex justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg border border-gray-200 mb-6 flex flex-col items-center">
      <div className="mb-4">
        <Upload
          name="avatar"
          listType="picture-card"
          showUploadList={false}
          beforeUpload={(file) => {
            handleAvatarUpload(file);
            return false; // Prevent default upload behavior
          }}
        >
          {avatarUrl ? (
            <Avatar
              src={avatarUrl}
              size={100}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Avatar
              size={100}
              style={{ backgroundColor: "#1890ff" }}
              icon={
                userData?.name ? (
                  <span className="text-2xl">{getInitials(userData.name)}</span>
                ) : (
                  <UserOutlined />
                )
              }
            />
          )}
        </Upload>
      </div>

      <h1 className="text-xl font-bold">{userData?.name || "User"}</h1>
      <p className="text-gray-500">{userData?.email || ""}</p>
      <p className="text-gray-500">
        {userData?.roles?.name || userData?.user_type?.name || "User"}
      </p>
      {userData?.location?.name && (
        <p className="text-gray-500 mt-1">Location: {userData.location.name}</p>
      )}
    </div>
  );
};

export default ProfileDetails;
